/* Zareen Jewels — Loved by you: story rail (previews + progress), full-screen story viewer */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  const FALLBACK_MS = 6000;

  const init = (root) => {
    if (!root || root.dataset.zjlReady) return;
    root.dataset.zjlReady = '1';
    const html = document.documentElement;

    /* ---------- rail: progress line + arrows ---------- */
    const rail = root.querySelector('[data-zjl-rail]');
    const bar = root.querySelector('[data-zjl-bar]');
    const prevBtn = root.querySelector('[data-zjl-prev]');
    const nextBtn = root.querySelector('[data-zjl-next]');
    const syncRail = () => {
      if (!rail) return;
      const max = rail.scrollWidth - rail.clientWidth;
      if (bar) {
        const w = rail.scrollWidth ? rail.clientWidth / rail.scrollWidth : 1;
        bar.style.setProperty('--w', `${Math.min(100, w * 100).toFixed(2)}%`);
        bar.style.setProperty('--x', `${((rail.scrollLeft / rail.clientWidth) * 100).toFixed(2)}%`);
      }
      if (prevBtn) prevBtn.disabled = rail.scrollLeft <= 4;
      if (nextBtn) nextBtn.disabled = rail.scrollLeft >= max - 4;
    };
    if (rail) {
      rail.addEventListener('scroll', () => requestAnimationFrame(syncRail), { passive: true });
      window.addEventListener('resize', syncRail);
      const page = (dir) => rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
      prevBtn && prevBtn.addEventListener('click', () => page(-1));
      nextBtn && nextBtn.addEventListener('click', () => page(1));
      syncRail();
    }

    /* ---------- muted previews play only while on screen ---------- */
    const previews = [...root.querySelectorAll('.zjl-story video')];
    const onScreen = new Set();
    let viewerOpen = false;
    const tryPlay = (v) => { const p = v.play(); p && p.catch(() => {}); };
    if (!reduce && !saveData && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting) { onScreen.add(v); if (!viewerOpen) tryPlay(v); }
          else { onScreen.delete(v); v.pause(); }
        });
      }, { threshold: 0.6 });
      previews.forEach((v) => io.observe(v));
    }

    /* ---------- viewer ---------- */
    const viewer = root.querySelector('[data-zjl-viewer]');
    const stories = [...root.querySelectorAll('[data-zjl-story]')];
    if (!viewer || !stories.length) return;
    const stage = viewer.querySelector('.zjl-viewer__stage');
    const video = viewer.querySelector('[data-zjl-video]');
    const barsWrap = viewer.querySelector('[data-zjl-bars]');
    const blur = viewer.querySelector('[data-zjl-blur]');
    const vHandle = viewer.querySelector('[data-zjl-v-handle]');
    const vCaption = viewer.querySelector('[data-zjl-v-caption]');
    const vAvatar = viewer.querySelector('[data-zjl-v-avatar]');
    const shop = viewer.querySelector('[data-zjl-shop]');
    const shopLabel = viewer.querySelector('[data-zjl-shop-label]');
    const muteBtn = viewer.querySelector('[data-zjl-mute]');
    const closeBtn = viewer.querySelector('.zjl-viewer__top [data-zjl-close]');

    barsWrap.innerHTML = stories.map(() => '<span class="zjl-viewer__bar"><i></i></span>').join('');
    const bars = [...barsWrap.children];

    let idx = 0, muted = false, paused = false, returnTo = null, raf = 0;
    let fallback = false, fbStart = 0, fbElapsed = 0;

    const setMute = (m) => {
      muted = m;
      video.muted = m;
      muteBtn.setAttribute('aria-pressed', String(m));
      muteBtn.setAttribute('aria-label', m ? 'Unmute' : 'Mute');
    };
    const setBar = (i, p) => bars[i] && bars[i].firstChild.style.setProperty('--p', p);

    const loop = () => {
      if (!viewerOpen) return;
      let p = 0;
      if (fallback) {
        const t = fbElapsed + (paused ? 0 : performance.now() - fbStart);
        p = Math.min(1, t / FALLBACK_MS);
        if (p >= 1) { go(1); return; }
      } else if (video.duration && isFinite(video.duration)) {
        p = video.currentTime / video.duration;
      }
      setBar(idx, p.toFixed(4));
      raf = requestAnimationFrame(loop);
    };

    const show = (i) => {
      idx = i;
      const btn = stories[i];
      const li = btn.closest('.zjl-story');
      const src = btn.querySelector('video');
      bars.forEach((b, n) => setBar(n, n < i ? 1 : 0));
      fallback = false; fbElapsed = 0; fbStart = performance.now();
      paused = false; viewer.classList.remove('is-paused');

      video.pause();
      video.innerHTML = src ? src.innerHTML : '';
      if (src && src.poster) { video.poster = src.poster; blur.style.backgroundImage = `url("${src.poster}")`; }
      else { video.removeAttribute('poster'); blur.style.backgroundImage = ''; }
      stage.style.setProperty('--zoom', li ? getComputedStyle(li).getPropertyValue('--zoom') || 1 : 1);

      const handle = btn.dataset.handle || '';
      vHandle.textContent = handle;
      vCaption.textContent = btn.dataset.caption || '';
      vAvatar.textContent = handle.replace('@', '').charAt(0).toUpperCase();
      vAvatar.hidden = !handle;
      if (btn.dataset.link) { shop.href = btn.dataset.link; shopLabel.textContent = btn.dataset.linkLabel || 'Shop the look'; shop.hidden = false; }
      else shop.hidden = true;

      if (!src) { fallback = true; fbStart = performance.now(); }
      else {
        video.muted = muted;
        video.load();
        const p = video.play();
        p && p.catch(() => {
          if (!muted) { setMute(true); const q = video.play(); q && q.catch(() => {}); }
        });
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(loop);
    };

    const go = (dir) => {
      const n = idx + dir;
      if (n < 0) { show(0); return; }
      if (n >= stories.length) { close(); return; }
      show(n);
    };

    const pause = () => {
      if (paused) return;
      paused = true; viewer.classList.add('is-paused');
      if (fallback) fbElapsed += performance.now() - fbStart; else video.pause();
    };
    const resume = () => {
      if (!paused) return;
      paused = false; viewer.classList.remove('is-paused');
      if (fallback) fbStart = performance.now(); else tryPlay(video);
    };

    const open = (i, opener) => {
      returnTo = opener;
      viewerOpen = true;
      previews.forEach((v) => v.pause());
      viewer.hidden = false;
      html.classList.add('zj-lock');
      setMute(false);
      show(i);
      requestAnimationFrame(() => requestAnimationFrame(() => viewer.classList.add('is-open')));
      setTimeout(() => closeBtn.focus({ preventScroll: true }), 50);
    };
    const close = () => {
      if (!viewerOpen) return;
      viewerOpen = false;
      cancelAnimationFrame(raf);
      video.pause();
      video.innerHTML = '';
      video.removeAttribute('poster');
      video.load();
      viewer.classList.remove('is-open', 'is-dragging');
      stage.style.removeProperty('--drag');
      html.classList.remove('zj-lock');
      setTimeout(() => { if (!viewerOpen) viewer.hidden = true; }, reduce ? 0 : 360);
      returnTo && returnTo.focus({ preventScroll: true });
      onScreen.forEach(tryPlay);
    };

    stories.forEach((btn, i) => btn.addEventListener('click', () => open(i, btn)));
    video.addEventListener('ended', () => go(1));
    /* no playable source (e.g. a browser without H.264) → keep the story moving on a timer */
    video.addEventListener('error', () => { if (viewerOpen && !fallback) { fallback = true; fbStart = performance.now(); fbElapsed = 0; } }, true);
    muteBtn.addEventListener('click', () => { setMute(!muted); if (!paused && !fallback) tryPlay(video); });

    /* taps, hold-to-pause, swipe down to close */
    let suppress = false, holdT = 0, held = false, dragging = false, y0 = 0, x0 = 0, dy = 0, active = false;
    viewer.querySelectorAll('[data-zjl-go]').forEach((b) =>
      b.addEventListener('click', () => { if (suppress) { suppress = false; return; } go(Number(b.dataset.zjlGo)); })
    );
    viewer.querySelectorAll('[data-zjl-close]').forEach((b) => b.addEventListener('click', close));

    stage.addEventListener('pointerdown', (e) => {
      if (e.target.closest('.zjl-viewer__top, .zjl-viewer__shop')) return;
      active = true; suppress = false; held = false; dragging = false; dy = 0;
      y0 = e.clientY; x0 = e.clientX;
      holdT = setTimeout(() => { held = true; pause(); }, 220);
    });
    stage.addEventListener('pointermove', (e) => {
      if (!active) return;
      dy = e.clientY - y0;
      if (!dragging && dy > 12 && Math.abs(dy) > Math.abs(e.clientX - x0)) { dragging = true; clearTimeout(holdT); viewer.classList.add('is-dragging'); }
      if (dragging) stage.style.setProperty('--drag', `${Math.max(0, dy)}px`);
    });
    const end = () => {
      if (!active) return;
      active = false;
      clearTimeout(holdT);
      if (dragging) {
        suppress = true;
        viewer.classList.remove('is-dragging');
        if (dy > 110) close(); else stage.style.removeProperty('--drag');
      } else if (held) { suppress = true; resume(); }
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
    stage.addEventListener('pointerleave', end);
    stage.addEventListener('contextmenu', (e) => { if (held) e.preventDefault(); });

    viewer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
      if (e.key !== 'Tab') return;
      const f = [...viewer.querySelectorAll('button, a[href]')].filter((n) => !n.hidden && n.offsetParent !== null && !n.classList.contains('zjl-viewer__zone'));
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden && viewerOpen) pause(); });
  };

  const boot = (scope) => scope.querySelectorAll('[data-zjl]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
